import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import HealthCheckSearchableSelect from 'features/HealthCheck/Common/HealthCheckSearchableSelect'
import { useEffect, useMemo, useState } from 'react'
import hotelApi from 'services/api/hotel'
import internalOnlyApi from 'services/api/internalOnly'

import { buildMerchantOptions } from './buildMerchantOptions'
import {
  buildMigrationSummaryItems,
  canSubmitManagementCompanyChange
} from './managementCompanyChangeHelpers'
import styles from './ManagementCompanyChangeDialog.module.scss'

const ManagementCompanyChangeDialog = ({
  displayId,
  merchantId,
  hotelName,
  onClose,
  onSuccess
}) => {
  const [merchantOptions, setMerchantOptions] = useState({})
  const [selectedMerchantId, setSelectedMerchantId] = useState('')
  const [typedDisplayId, setTypedDisplayId] = useState('')
  const [isLoadingMerchants, setIsLoadingMerchants] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [result, setResult] = useState(null)

  useEffect(() => {
    let isMounted = true

    setIsLoadingMerchants(true)
    hotelApi
      .getHotelGroups()
      .then(data => {
        if (!isMounted) {
          return
        }

        setMerchantOptions(buildMerchantOptions(data, merchantId))
      })
      .catch(error => {
        if (!isMounted) {
          return
        }

        setErrorMessage(
          error?.message || 'Unable to load management companies. Please try again.'
        )
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingMerchants(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [merchantId])

  const selectedMerchantName = useMemo(() => {
    if (!selectedMerchantId) {
      return ''
    }

    return merchantOptions[selectedMerchantId] || ''
  }, [merchantOptions, selectedMerchantId])

  const canSubmit = canSubmitManagementCompanyChange({
    isSubmitting,
    isLoadingMerchants,
    selectedMerchantId,
    typedDisplayId,
    displayId
  })

  async function handleConfirm() {
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage('')

    try {
      const response = await internalOnlyApi.migrateSubsidiary({
        merchantId: Number(selectedMerchantId)
      })
      setResult(response)
      if (onSuccess) {
        onSuccess(response)
      }
    } catch (error) {
      setErrorMessage(
        error?.message || 'Management company change failed. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (result) {
    const summaryItems = buildMigrationSummaryItems(result, displayId)

    return (
      <ConfirmationDialog
        title="Management company change complete"
        positiveMessage="Close"
        onClose={onClose}
        onConfirm={onClose}
        hasCloseButton
        maxWidth={560}
      >
        <p className={styles.summaryTitle}>Migration summary</p>
        <ul className={styles.summaryList}>
          {summaryItems.map(item => (
            <li key={item.label}>
              {item.label}: <strong>{item.value}</strong>
            </li>
          ))}
        </ul>
      </ConfirmationDialog>
    )
  }

  return (
    <ConfirmationDialog
      title="Management Company Change"
      positiveMessage={isSubmitting ? 'Changing...' : 'Management Company Change'}
      negativeMessage="Cancel"
      onClose={onClose}
      onReject={onClose}
      onConfirm={handleConfirm}
      isButtonDisabled={!canSubmit}
      hasDangerousAction
      hasCloseButton
      allowOverflow
      maxWidth={560}
      contentAbove={
        <div className={styles.contentAboveContainer}>
          <i className={styles.warningIcon} />
          <p className={styles.warningText}>
            Caution: this action is irreversible. Users, billing, invoices, and payments
            will be archived under the current hotel DisplayId.
          </p>
        </div>
      }
    >
      <p className={styles.infoText}>
        Moving <strong>{hotelName}</strong> (DisplayId <strong>#{displayId}</strong>) to a
        new management company will:
      </p>
      <ul className={styles.infoList}>
        <li>Move the live hotel to the selected management company</li>
        <li>Assign a new DisplayId to the live hotel</li>
        <li>Archive users, billing, invoices, and payments under the old DisplayId</li>
        <li>
          Keep invoice-period configs, prepay-rate options, and prepay-period histories on
          both hotel records
        </li>
      </ul>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>New management company</label>
        {isLoadingMerchants ? (
          <div className={styles.loadingText}>Loading management companies...</div>
        ) : (
          <HealthCheckSearchableSelect
            value={selectedMerchantId}
            options={merchantOptions}
            onChange={value => {
              setSelectedMerchantId(value)
              setErrorMessage('')
            }}
          />
        )}
        {selectedMerchantName ? (
          <div className={styles.fieldHelp}>Selected: {selectedMerchantName}</div>
        ) : (
          <div className={styles.fieldHelp}>
            Search and select an active management company different from the current one.
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          Type current DisplayId #{displayId} to verify
        </label>
        <input
          className={styles.verifyInput}
          type="text"
          value={typedDisplayId}
          onChange={e => {
            setTypedDisplayId(e.target.value)
            setErrorMessage('')
          }}
          placeholder={String(displayId)}
          autoComplete="off"
        />
      </div>

      {errorMessage ? <div className={styles.errorText}>{errorMessage}</div> : null}
    </ConfirmationDialog>
  )
}

export default ManagementCompanyChangeDialog
